import download from "downloadjs";
import * as XLSX from "xlsx-js-style";
import { DateTime } from "luxon";
import t from "../../../i18n";
import { translationLocale } from "../../../i18n/locale";
import type { Translation } from "../../../api/useTranslations";
import type { Reparability } from "../../../api/Reparability";
import {
  calculateCost,
  formatMonetaryValue,
  IMPACT_RANGES,
  buildReparabilityMatrix,
  buildCellEntry,
  EXCEL_CONSTANTS,
  type MatrixReporting as SharedMatrixReporting,
  getExcelBackgroundColor,
  type CellEntry,
  type ImpactRange,
  REPARABILITY_TIME_TO_FIX,
  getReparabilityCategories,
  type MatrixFilters,
  COST_PER_MWH,
} from "./faultMappingUtils";

type MatrixReporting = SharedMatrixReporting;

const getDisplayName = (entry: CellEntry): string => entry.itemName;

const getCellData = (
  matrix: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  category: string,
  rangeLabel: string,
): CellEntry[] => matrix[category]?.[rangeLabel] || [];

const getUniqueFaultsInCategory = (cellData: CellEntry[]): Set<string> => {
  const uniqueFaults = new Set<string>();
  for (const entry of cellData) {
    uniqueFaults.add(getDisplayName(entry));
  }
  return uniqueFaults;
};

const calculateFaultImpacts = (
  matrix: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  reparabilityCategories: Reparability[],
  impactRange: ImpactRange,
): Map<string, number> => {
  const faultImpacts = new Map<string, number>();
  for (const category of reparabilityCategories) {
    const cellData = getCellData(matrix, category, impactRange.label);
    for (const entry of cellData) {
      const displayName = getDisplayName(entry);
      const currentImpact = faultImpacts.get(displayName) || 0;
      faultImpacts.set(displayName, currentImpact + entry.impactValue);
    }
  }
  return faultImpacts;
};

const getMaxFaultsInRange = (
  matrix: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  reparabilityCategories: Reparability[],
  impactRange: ImpactRange,
): number => {
  let maxFaultsInThisRange = 0;
  for (const category of reparabilityCategories) {
    const cellData = getCellData(matrix, category, impactRange.label);
    const uniqueFaults = getUniqueFaultsInCategory(cellData);
    if (uniqueFaults.size > maxFaultsInThisRange) {
      maxFaultsInThisRange = uniqueFaults.size;
    }
  }
  return maxFaultsInThisRange;
};
const createFaultEntries = (
  matrix: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  category: string,
  impactRange: ImpactRange,
  rowIndex: number,
  faultImpacts: Map<string, number>,
  repairIndex: number,
): (string | number)[] => {
  const cellData = getCellData(matrix, category, impactRange.label);

  const uniqueFaultsInCategory = getUniqueFaultsInCategory(cellData);
  const sortedFaultsInCategory = Array.from(uniqueFaultsInCategory).sort((a, b) => {
    const impactA = faultImpacts.get(a) || 0;
    const impactB = faultImpacts.get(b) || 0;
    return impactB - impactA;
  });

  const faultName =
    rowIndex < sortedFaultsInCategory.length ? sortedFaultsInCategory[rowIndex] : undefined;

  if (faultName && cellData.length > 0) {
    const faultEntries = cellData.filter((entry: CellEntry) => {
      const displayName = getDisplayName(entry);
      return displayName === faultName;
    });

    if (faultEntries.length > 0) {
      const trigramList = faultEntries.map((entry: CellEntry) => {
        let trigram = entry.trigram;
        if (entry.isRecurrentFault) {
          trigram += "*";
        }
        if (
          entry.hasUnknownReparability &&
          repairIndex === EXCEL_CONSTANTS.SPECIAL_COLUMNS.ATF_CATEGORY_INDEX
        ) {
          trigram += "?";
        }

        // Display "<1 MW" for impacts under 1 MW, otherwise show rounded value
        const impactDisplay =
          entry.impactValue < 1 ? "<1 MW" : `${entry.impactValue.toFixed(0)} MW`;
        trigram += ` (${impactDisplay})`;
        return trigram;
      });
      const trigrams = trigramList.join(", ");
      const totalImpact = faultEntries.reduce(
        (sum: number, entry: CellEntry) => sum + entry.impactValue,
        0,
      );

      // Format total impact: show "< 1MW" for total impacts less than 0.1 MW, otherwise show rounded value
      const formattedTotalImpact = totalImpact < 0.1 ? "< 1MW" : parseFloat(totalImpact.toFixed(0));

      return [faultName, trigrams, formattedTotalImpact];
    }
  }

  return ["", "", ""];
};

const createDetailedDataSheet = (
  matrixData: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  reparabilityCategories: Reparability[],
  impactRanges: ImpactRange[],
  reparabilityDescriptions: string[],
): (string | number)[][] => {
  const detailedData: (string | number)[][] = [];
  detailedData.push([
    "Catégorie de Réparabilité",
    "Plage d'Impact",
    "Nom de la Défaillance",
    "Nom de la Centrale",
    "Trigramme de la Centrale",
    "Impact (MW)",
    "Réparabilité Inconnue",
    "Défaillance Récurrente",
  ]);

  for (const category of reparabilityCategories) {
    const categoryIndex = reparabilityCategories.indexOf(category);
    const translatedCategory = reparabilityDescriptions[categoryIndex] || category;

    for (const range of impactRanges) {
      const cellData = getCellData(matrixData, category, range.label);
      for (const entry of cellData) {
        detailedData.push([
          translatedCategory,
          range.label,
          entry.itemName,
          entry.plantName,
          entry.trigram,
          parseFloat(entry.impactValue.toFixed(1)),
          entry.hasUnknownReparability ? "Oui" : "Non",
          entry.isRecurrentFault ? "Oui" : "Non",
        ]);
      }
    }
  }

  return detailedData;
};

const calculateFinancialImpacts = (
  matrixData: { [reparabilityCategory: string]: { [impactRange: string]: CellEntry[] } },
  reparabilityCategories: Reparability[],
  impactRanges: ImpactRange[],
): { [category: string]: { [range: string]: { totalCost: number; failureCount: number } } } => {
  const cellCosts: {
    [category: string]: { [range: string]: { totalCost: number; failureCount: number } };
  } = {};

  for (const category of reparabilityCategories) {
    cellCosts[category] = {};

    for (const range of impactRanges) {
      const cellData = getCellData(matrixData, category, range.label);
      let totalCost = 0;
      let failureCount = 0;

      const failureGroups = new Map<string, CellEntry[]>();
      for (const entry of cellData) {
        const displayName = getDisplayName(entry);
        if (!failureGroups.has(displayName)) {
          failureGroups.set(displayName, []);
        }
        failureGroups.get(displayName)!.push(entry);
      }

      for (const [_, entries] of failureGroups) {
        failureCount++;
        const totalImpact = entries.reduce((sum, entry) => sum + entry.impactValue, 0);
        const cost = calculateCost(totalImpact, category); // category is now correctly typed as Reparability
        totalCost += cost;
      }

      cellCosts[category][range.label] = { totalCost, failureCount };
    }
  }

  return cellCosts;
};

export const exportReparabilityMatrix = (
  reporting: MatrixReporting,
  translation: Translation,
  filters: MatrixFilters,
  reparabilityDescriptions: string[],
): void => {
  if (!reporting || !reporting.periods) {
    return;
  }
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const timeStr = `${now.getHours().toString().padStart(2, "0")}-${now.getMinutes().toString().padStart(2, "0")}`;

  const fileName = `reparability-matrix-export-${dateStr}_${timeStr}.xlsx`;

  const reparabilityCategories = getReparabilityCategories();

  const impactRanges = IMPACT_RANGES;

  const matrixResult = buildReparabilityMatrix(
    reporting,
    filters,
    {} as Record<string, string>,
    (_key: string, value: Reparability) => t("reparability", value, translation),
    buildCellEntry,
  );

  const { matrix: matrixData } = matrixResult;

  const wb = XLSX.utils.book_new();

  let maxFaultsPerCell = 0;
  for (const category of reparabilityCategories) {
    for (const range of impactRanges) {
      const cellData = matrixData[category]?.[range.label] || [];
      if (cellData.length > maxFaultsPerCell) {
        maxFaultsPerCell = cellData.length;
      }
    }
  }

  const expandedMatrix: (string | number)[][] = [];

  // Format the month name for display
  const monthName = filters.selectedMonth
    ? DateTime.fromObject({ year: filters.selectedYear, month: filters.selectedMonth })
        .setLocale(translationLocale)
        .toFormat("MMMM")
    : filters.selectedMonth;

  expandedMatrix.push([
    `Matrice de Réparabilité pour le mois de : ${monthName} ${filters.selectedYear}`,
  ]);
  expandedMatrix.push([`Généré le : ${dateStr} ${timeStr}`]);
  expandedMatrix.push([]);

  const mainHeaderRow = ["Plage d'Impact"];
  for (const [index] of reparabilityCategories.entries()) {
    mainHeaderRow.push(`${reparabilityDescriptions[index]}`);
    mainHeaderRow.push("Tranche");
    mainHeaderRow.push("Impact (MW)");
  }
  expandedMatrix.push(mainHeaderRow);

  const reversedImpactRanges = [...impactRanges].reverse();

  for (const impactRange of reversedImpactRanges) {
    const faultImpacts = calculateFaultImpacts(matrixData, reparabilityCategories, impactRange);

    const maxFaultsInThisRange = getMaxFaultsInRange(
      matrixData,
      reparabilityCategories,
      impactRange,
    );

    const rowsToCreate = Math.max(EXCEL_CONSTANTS.MIN_ROWS_PER_IMPACT_RANGE, maxFaultsInThisRange);

    for (let rowIndex = 0; rowIndex < rowsToCreate; rowIndex++) {
      const row: (string | number)[] = [];

      if (rowIndex === 0) {
        row.push(impactRange.label);
      } else {
        row.push("");
      }

      for (let repairIndex = 0; repairIndex < reparabilityCategories.length; repairIndex++) {
        const category = reparabilityCategories[repairIndex];
        if (!category) continue;

        const entries = createFaultEntries(
          matrixData,
          category,
          impactRange,
          rowIndex,
          faultImpacts,
          repairIndex,
        );
        row.push(...entries);
      }

      expandedMatrix.push(row);
    }
  }

  expandedMatrix.push([]);
  expandedMatrix.push(["LÉGENDE :"]);
  expandedMatrix.push([
    "Nom de la Défaillance | Trigrammes Centrales (Impact MW Individuel) | Impact Total (MW) pour chaque catégorie de réparabilité",
  ]);
  expandedMatrix.push(["— indique aucune donnée pour cette plage d'impact"]);
  expandedMatrix.push(["* indique une tranche avec une défaillance récurrente du mois précédent"]);
  expandedMatrix.push([
    "? indique une tranche dont la défaillance a une réparabilité inconnue (déplacée vers la colonne ATF)",
  ]);

  const ws = XLSX.utils.aoa_to_sheet(expandedMatrix);

  const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

  const headerRowIndex = EXCEL_CONSTANTS.HEADER_ROW_INDEX;
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
    if (!ws[cellRef]) continue;

    const isCornerCell = col === 0;

    const isCategoryHeader =
      col > 0 && (col - 1) % EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY === 0;

    ws[cellRef].s = {
      fill: {
        fgColor: {
          rgb: EXCEL_CONSTANTS.COLORS.CATEGORY_HEADER,
        },
      },
      font: {
        bold: true,
        size: isCornerCell
          ? EXCEL_CONSTANTS.FONT_SIZES.CORNER_CELL
          : isCategoryHeader
            ? EXCEL_CONSTANTS.FONT_SIZES.CATEGORY_HEADER
            : EXCEL_CONSTANTS.FONT_SIZES.SUB_HEADER,
        color: { rgb: EXCEL_CONSTANTS.COLORS.PRIMARY_TEXT },
      },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
        bottom: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
        left: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
        right: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
      },
    };
  }

  for (let row = headerRowIndex + 1; row < range.e.r + 1; row++) {
    const rowData = expandedMatrix[row];
    if (!rowData || rowData.length === 0) continue;

    const impactRangeLabel = rowData[0] as string;
    let impactIndex = -1;
    let currentImpactRangeLabel = "";

    if (impactRangeLabel && impactRangeLabel !== "") {
      impactIndex = impactRanges.findIndex(r => r.label === impactRangeLabel);
      currentImpactRangeLabel = impactRangeLabel;
    } else {
      for (let prevRow = row - 1; prevRow >= headerRowIndex + 1; prevRow--) {
        const prevRowData = expandedMatrix[prevRow];
        if (prevRowData && prevRowData[0] && prevRowData[0] !== "") {
          impactIndex = impactRanges.findIndex(r => r.label === prevRowData[0]);
          currentImpactRangeLabel = prevRowData[0] as string;
          break;
        }
      }
    }

    let isLastRowOfImpactRange = false;
    if (currentImpactRangeLabel) {
      const nextRow = row + 1;
      if (nextRow >= range.e.r + 1) {
        isLastRowOfImpactRange = true;
      } else {
        const nextRowData = expandedMatrix[nextRow];
        if (
          nextRowData &&
          nextRowData[0] &&
          nextRowData[0] !== "" &&
          nextRowData[0] !== currentImpactRangeLabel
        ) {
          isLastRowOfImpactRange = true;
        } else if (!nextRowData || nextRowData.length === 0) {
          isLastRowOfImpactRange = true;
        }
      }
    }

    for (let col = 0; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellRef]) continue;

      if (col === 0) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: EXCEL_CONSTANTS.COLORS.CATEGORY_HEADER } },
          font: {
            bold: true,
            size: EXCEL_CONSTANTS.FONT_SIZES.IMPACT_LABEL,
            color: { rgb: EXCEL_CONSTANTS.COLORS.PRIMARY_TEXT },
          },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            bottom: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            left: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            right: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
          },
        };
      } else {
        const repairIndex = Math.floor(
          (col - 1) / EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY,
        );
        const columnType = (col - 1) % EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY;

        if (impactIndex >= 0 && repairIndex < reparabilityCategories.length) {
          // For data cells, always check if there's data in any column of the same reparability category
          let hasDataInCategory = false;
          const categoryStartCol =
            1 + repairIndex * EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY;
          for (
            let checkCol = categoryStartCol;
            checkCol < categoryStartCol + EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY;
            checkCol++
          ) {
            const checkCellRef = XLSX.utils.encode_cell({ r: row, c: checkCol });
            if (
              ws[checkCellRef] &&
              ws[checkCellRef].v &&
              ws[checkCellRef].v !== "" &&
              ws[checkCellRef].v !== "—"
            ) {
              hasDataInCategory = true;
              break;
            }
          }

          const backgroundColor = getExcelBackgroundColor(
            repairIndex,
            impactIndex,
            hasDataInCategory,
          );

          const isFirstRowOfImpactRange = impactRangeLabel && impactRangeLabel !== "";

          const isFirstDataColumn = col === 1;

          const isFirstColumnOfNewReparability =
            col > 1 && (col - 1) % EXCEL_CONSTANTS.COLUMNS_PER_REPARABILITY_CATEGORY === 0;

          const isPertes5MWBorder =
            currentImpactRangeLabel === "3 MW < Pertes < 5 MW" && isFirstRowOfImpactRange;

          const isAfterFixOnline = col === EXCEL_CONSTANTS.SPECIAL_COLUMNS.AFTER_FIX_ONLINE;

          const isLastColumn = col === range.e.c;

          ws[cellRef].s = {
            fill: { fgColor: { rgb: backgroundColor } },
            font: {
              size: EXCEL_CONSTANTS.FONT_SIZES.DATA_CELL,
              color: {
                rgb: hasDataInCategory
                  ? EXCEL_CONSTANTS.COLORS.DATA_TEXT
                  : EXCEL_CONSTANTS.COLORS.MUTED_TEXT,
              },
              bold: columnType === 0,
            },
            alignment: {
              horizontal: columnType === 2 ? "right" : "left",
              vertical: "center",
              wrapText: true,
            },
            border: {
              top: {
                style: isFirstRowOfImpactRange || isPertes5MWBorder ? "thick" : "thin",
                color: {
                  rgb:
                    isFirstRowOfImpactRange || isPertes5MWBorder
                      ? EXCEL_CONSTANTS.COLORS.THICK_BORDER
                      : EXCEL_CONSTANTS.COLORS.THIN_BORDER,
                },
              },
              bottom: {
                style: isLastRowOfImpactRange ? "thick" : "thin",
                color: {
                  rgb: isLastRowOfImpactRange
                    ? EXCEL_CONSTANTS.COLORS.THICK_BORDER
                    : EXCEL_CONSTANTS.COLORS.THIN_BORDER,
                },
              },
              left: {
                style:
                  isFirstDataColumn || isFirstColumnOfNewReparability || isAfterFixOnline
                    ? "thick"
                    : "thin",
                color: {
                  rgb:
                    isFirstDataColumn || isFirstColumnOfNewReparability || isAfterFixOnline
                      ? EXCEL_CONSTANTS.COLORS.THICK_BORDER
                      : EXCEL_CONSTANTS.COLORS.THIN_BORDER,
                },
              },
              right: {
                style: isLastColumn ? "thick" : "thin",
                color: {
                  rgb: isLastColumn
                    ? EXCEL_CONSTANTS.COLORS.THICK_BORDER
                    : EXCEL_CONSTANTS.COLORS.THIN_BORDER,
                },
              },
            },
          };
        }
      }
    }
  }

  const colWidths: { wch: number }[] = [{ wch: EXCEL_CONSTANTS.COLUMN_WIDTHS.IMPACT_RANGE }];
  for (let i = 0; i < reparabilityCategories.length; i++) {
    colWidths.push({ wch: EXCEL_CONSTANTS.COLUMN_WIDTHS.FAILURE_NAME });
    colWidths.push({ wch: EXCEL_CONSTANTS.COLUMN_WIDTHS.PLANT_TRIGRAM });
    colWidths.push({ wch: EXCEL_CONSTANTS.COLUMN_WIDTHS.IMPACT_VALUE });
  }
  ws["!cols"] = colWidths;

  ws["!rows"] = [];
  for (let i = 0; i < expandedMatrix.length; i++) {
    if (i > headerRowIndex) {
      ws["!rows"][i] = { hpt: EXCEL_CONSTANTS.ROW_HEIGHTS.STANDARD_DATA };
    } else if (i === headerRowIndex) {
      ws["!rows"][i] = { hpt: EXCEL_CONSTANTS.ROW_HEIGHTS.HEADER };
    }
  }

  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];
  let currentRow = headerRowIndex + 1;

  for (const impactRange of reversedImpactRanges) {
    let maxFaultsInThisRange = 0;
    for (const category of reparabilityCategories) {
      const cellData = matrixData[category]?.[impactRange.label] || [];
      const uniqueFaultsInCategory = new Set<string>();
      if (cellData && cellData.length > 0) {
        for (const entry of cellData) {
          const displayName = getDisplayName(entry);
          uniqueFaultsInCategory.add(displayName);
        }
      }
      if (uniqueFaultsInCategory.size > maxFaultsInThisRange) {
        maxFaultsInThisRange = uniqueFaultsInCategory.size;
      }
    }

    const faultCount = Math.max(EXCEL_CONSTANTS.MIN_ROWS_PER_IMPACT_RANGE, maxFaultsInThisRange);

    if (faultCount > 1) {
      merges.push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow + faultCount - 1, c: 0 },
      });
    }

    currentRow += faultCount;
  }

  if (merges.length > 0) {
    ws["!merges"] = merges;
  }

  XLSX.utils.book_append_sheet(wb, ws, "Matrice de Réparabilité");

  const detailedData = createDetailedDataSheet(
    matrixData,
    reparabilityCategories,
    impactRanges,
    reparabilityDescriptions,
  );
  const detailedWs = XLSX.utils.aoa_to_sheet(detailedData);
  XLSX.utils.book_append_sheet(wb, detailedWs, "Données Détaillées");

  // ================================
  // Create Financial Summary Worksheet
  // ================================

  const financialData: (string | number)[][] = [];
  financialData.push(["MATRICE D'IMPACT FINANCIER"]);
  financialData.push([`Généré le : ${dateStr} ${timeStr}`]);
  financialData.push([]);

  const cellCosts = calculateFinancialImpacts(matrixData, reparabilityCategories, impactRanges);
  const matrixHeaderRow = ["Plage d'Impact"];

  for (const [index] of reparabilityCategories.entries()) {
    matrixHeaderRow.push(reparabilityDescriptions[index] ?? "");
  }
  matrixHeaderRow.push("Total Ligne");
  financialData.push(matrixHeaderRow);

  const financialReversedRanges = [...impactRanges].reverse();

  for (const range of financialReversedRanges) {
    const matrixRow: (string | number)[] = [range.label];
    let rowTotal = 0;

    for (const category of reparabilityCategories) {
      const cellData = cellCosts[category]?.[range.label];
      const cost = cellData ? cellData.totalCost : 0;
      rowTotal += cost;
      matrixRow.push(formatMonetaryValue(cost));
    }

    matrixRow.push(formatMonetaryValue(rowTotal));
    financialData.push(matrixRow);
  }

  const columnTotalsRow: (string | number)[] = ["Total Colonne"];
  let grandTotal = 0;

  for (const category of reparabilityCategories) {
    let columnTotal = 0;
    for (const range of impactRanges) {
      const cellData = cellCosts[category]?.[range.label];
      if (cellData) {
        columnTotal += cellData.totalCost;
      }
    }
    grandTotal += columnTotal;
    columnTotalsRow.push(formatMonetaryValue(columnTotal));
  }

  columnTotalsRow.push(formatMonetaryValue(grandTotal));
  financialData.push(columnTotalsRow);

  financialData.push([]);
  financialData.push(["MÉTHODOLOGIE DE CALCUL DES COÛTS"]);
  financialData.push([`Coût = Impact (MW) × Temps de Réparation (heures) × ${COST_PER_MWH} €/MWh`]);
  financialData.push([
    `${t("reparability", "REPARABILITY_0", translation)} : ${REPARABILITY_TIME_TO_FIX.REPARABILITY_0} heures (3 semaines)`,
  ]);
  financialData.push([
    `${t("reparability", "REPARABILITY_1", translation)} : ${REPARABILITY_TIME_TO_FIX.REPARABILITY_1} heures (2 mois)`,
  ]);
  financialData.push([
    `${t("reparability", "REPARABILITY_2", translation)} : ${REPARABILITY_TIME_TO_FIX.REPARABILITY_2} heures`,
  ]);

  const financialWs = XLSX.utils.aoa_to_sheet(financialData);

  const financialRange = XLSX.utils.decode_range(financialWs["!ref"] || "A1");

  for (let row = 0; row <= financialRange.e.r; row++) {
    for (let col = 0; col <= financialRange.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!financialWs[cellRef]) continue;

      const cellValue = financialWs[cellRef].v;

      const isTitle = row === 0 && col === 0;
      const isMatrixHeader = row === 3;
      const isRowTotal = col === financialRange.e.c && row > 3 && row < financialRange.e.r;
      const isColumnTotal = row === financialRange.e.r && col > 0;
      const isGrandTotal = row === financialRange.e.r && col === financialRange.e.c;
      const isRowHeader = col === 0 && row > 3;
      const isMethodology =
        (typeof cellValue === "string" && cellValue.includes("MÉTHODOLOGIE")) ||
        (typeof cellValue === "string" && cellValue.includes("Coût =")) ||
        (typeof cellValue === "string" && cellValue.includes("heures"));

      if (isTitle) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: EXCEL_CONSTANTS.COLORS.PRIMARY_TEXT } },
          font: { bold: true, color: { rgb: "FFFFFF" }, size: 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      } else if (isMatrixHeader) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: EXCEL_CONSTANTS.COLORS.CATEGORY_HEADER } },
          font: {
            bold: true,
            color: { rgb: EXCEL_CONSTANTS.COLORS.PRIMARY_TEXT },
            size: EXCEL_CONSTANTS.FONT_SIZES.FINANCIAL_DATA,
          },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            bottom: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            left: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
            right: { style: "thick", color: { rgb: EXCEL_CONSTANTS.COLORS.THICK_BORDER } },
          },
        };
      } else if (isGrandTotal) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: "FFEB3B" } },
          font: {
            bold: true,
            color: { rgb: EXCEL_CONSTANTS.COLORS.DATA_TEXT },
            size: EXCEL_CONSTANTS.FONT_SIZES.FINANCIAL_HEADER,
          },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thick", color: { rgb: "FF9800" } },
            bottom: { style: "thick", color: { rgb: "FF9800" } },
            left: { style: "thick", color: { rgb: "FF9800" } },
            right: { style: "thick", color: { rgb: "FF9800" } },
          },
        };
      } else if (isRowTotal || isColumnTotal) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: "FFF3E0" } },
          font: { bold: true, color: { rgb: "E65100" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "medium", color: { rgb: "FF9800" } },
            bottom: { style: "medium", color: { rgb: "FF9800" } },
            left: { style: "medium", color: { rgb: "FF9800" } },
            right: { style: "medium", color: { rgb: "FF9800" } },
          },
        };
      } else if (isRowHeader) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: "F3E5F5" } },
          font: { bold: true, color: { rgb: "7B1FA2" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "9C27B0" } },
            bottom: { style: "thin", color: { rgb: "9C27B0" } },
            left: { style: "thick", color: { rgb: "9C27B0" } },
            right: { style: "thin", color: { rgb: "9C27B0" } },
          },
        };
      } else if (isMethodology) {
        financialWs[cellRef].s = {
          fill: { fgColor: { rgb: "F5F5F5" } },
          font: { italic: true, color: { rgb: "424242" } },
          alignment: { horizontal: "left", vertical: "center" },
        };
      } else if (row > 3 && row < financialRange.e.r && col > 0 && col < financialRange.e.c) {
        financialWs[cellRef].s = {
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "E0E0E0" } },
            bottom: { style: "thin", color: { rgb: "E0E0E0" } },
            left: { style: "thin", color: { rgb: "E0E0E0" } },
            right: { style: "thin", color: { rgb: "E0E0E0" } },
          },
        };
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, financialWs, "Résumé Financier");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;",
  });
  download(blob, fileName);
};
